import { Container, EventMode, FederatedPointerEvent, Graphics, IDestroyOptions, Point, Renderer, Sprite, Text, TextStyle, Texture, utils } from "pixi.js";
import { MyBuildingsResourcePackage } from "../resources/MyBuildingsResourcePackage";
import { IBuildingModel } from "../../../common/models/Building/IBuildingModel";
import { nameof } from "bindable-data";
import { GameFont } from "../../../common/GameFont";
import { CommonResourcesPackage } from "../../../common/resources/CommonResourcesPackage";
import { BuildingInfo } from "./BuildingInfo";
import { BuildingProgress } from "./BuildingProgress";
import { UpgradeResourcePackage } from "../resources/UpgradeResourcePackage";
import { ListInButton } from "./ListInButton";
import { SeeBuildingInButton } from "./SeeBuildingInButton";
import { OutlineFilter } from "pixi-filters";
import { Event } from "event-system";
import gsap from "gsap";
import { ArcGradient } from "./ArcGradient";
import { MaskContainer } from "../../../common/components/MaskContainer";
import { UpgradeButton } from "./UpgradeButton";
import { PurchaseBuildingButton } from "./PurchaseBuildingButton";
import { IPlayerModel } from "../../../common/models/IPlayerModel";
import { BuildingRightsType } from "../../../../networking/data/responses/user/BuildingRightsType";
import { DynamicResourcesPackage } from "../../../../../resources/DynamicResourcesPackage";
import { PriceHistoryButton } from "../../../common/components/PriceHistoryButton";
import { HBox } from "../../../common/components/ui/HBox";
import { StringFormatExtention } from "../../../../../extensions/StringFormatExtention";
import { PiggyClubMultiplier } from "../../../common/components/PiggyClubMultiplier";

export class Building extends Container {
    private static readonly BUILDING_SIDE_SIZE = 820;
    private static readonly UPGRADE_BUILDING_SIDE_SIZE = 1300;
    private static readonly ROUND_MASK_RADIUS = 305;
    private static readonly RECTANGLE_MASK_HEIGHT = 1028;
    private static readonly RECTANGLE_MASK_UPGRADE_HEIGHT = 436;
    private static readonly PURCHASE_PRICE = "PURCHASE PRICE: ";

    public readonly onBuildingClick: Event<void>;

    private readonly myBuildingsResources: MyBuildingsResourcePackage;
    private readonly upgradeResources: UpgradeResourcePackage;
    private readonly commonResources: CommonResourcesPackage;
    private readonly myBuildingsDynamicResources: DynamicResourcesPackage;
    private readonly buildingModel: IBuildingModel;
    private readonly referenceWidth: number;
    private readonly renderer: Renderer;
    private readonly playerModel: IPlayerModel;
    private readonly isFirstShow: boolean;

    public get id(): string {
        return this.buildingModel.id.value;
    }

    public get onPriceHistoryClick(): Event<PriceHistoryButton, FederatedPointerEvent> {
        return this.priceHistoryButton.onUp;
    }

    public get onPriceHistoryButtonDown(): Event<PriceHistoryButton, FederatedPointerEvent> {
        return this.priceHistoryButton?.onDown;
    }

    public get onPriceHistoryButtonOut(): Event<PriceHistoryButton, FederatedPointerEvent> {
        return utils.isMobile.any ? this.priceHistoryButton?.onUpOut : this.priceHistoryButton?.onOut;
    }

    public get onListInClick(): Event<ListInButton, FederatedPointerEvent> {
        return this.listInButton.onUp;
    }

    public get onListInButtonDown(): Event<ListInButton, FederatedPointerEvent> {
        return this.listInButton?.onDown;
    }

    public get onListInButtonOut(): Event<ListInButton, FederatedPointerEvent> {
        return utils.isMobile.any ? this.listInButton?.onUpOut : this.listInButton?.onOut;
    }

    public get onSeeBuildingInClick(): Event<SeeBuildingInButton, FederatedPointerEvent> {
        return this.seeBuildingInButton.onUp;
    }

    public get onSeeBuildingButtonDown(): Event<SeeBuildingInButton, FederatedPointerEvent> {
        return this.seeBuildingInButton?.onDown;
    }

    public get onSeeBuildingButtonOut(): Event<SeeBuildingInButton, FederatedPointerEvent> {
        return utils.isMobile.any ? this.seeBuildingInButton?.onUpOut : this.seeBuildingInButton?.onOut;
    }

    public get onPurchaseBuildingClick(): Event<PurchaseBuildingButton, FederatedPointerEvent> {
        return this.purchaseBuildingButton.onUp;
    }

    public get onPurchaseButtonDown(): Event<PurchaseBuildingButton, FederatedPointerEvent> {
        return this.purchaseBuildingButton?.onDown;
    }

    public get onPurchaseButtonOut(): Event<PurchaseBuildingButton, FederatedPointerEvent> {
        return utils.isMobile.any ? this.purchaseBuildingButton?.onUpOut : this.purchaseBuildingButton?.onOut;
    }

    public get onUpgradeClick(): Event<UpgradeButton, FederatedPointerEvent> {
        return this.upgradeButton?.onUp;
    }

    public get onUpgradeButtonDown(): Event<UpgradeButton, FederatedPointerEvent> {
        return this.upgradeButton?.onDown;
    }

    public get onUpgradeButtonOut(): Event<UpgradeButton, FederatedPointerEvent> {
        return utils.isMobile.any ? this.upgradeButton?.onUpOut : this.upgradeButton?.onOut;
    }

    public get buildingProgress(): BuildingProgress {
        return this.buildingProgressProperty;
    }

    public get seeBuilding(): SeeBuildingInButton {
        return this.seeBuildingInButton;
    }

    public get listIn(): ListInButton {
        return this.listInButton;
    }

    public set enabled(enabled: boolean) {
        this.upgradeButton.eventMode = enabled ? "static" : "auto";
        this.building.eventMode = enabled ? "dynamic" : "auto";
    }

    public set enabledButtons(enabled: boolean) {
        this.listInButton.enabled = enabled;
        this.seeBuildingInButton.enabled = enabled;
    }

    public override set eventMode(eventMode: EventMode) {
        super.eventMode = eventMode;
        if (this.buildingModel.upgrades.length !== 0) {
            this.building.eventMode = eventMode;
        }
    }

    private buildingContainer: Container;
    private addressPlaceholder: Sprite;
    private address: Text;
    private piggyClubMultiplier: PiggyClubMultiplier;
    private locationPlaceholder: Graphics;
    private location: Text;
    private listInButton: ListInButton;
    private initialCostText: Text;
    private priceHistoryButton: PriceHistoryButton;
    private seeBuildingInButton: SeeBuildingInButton;
    private purchaseBuildingButton: PurchaseBuildingButton;
    private buildingGlow: Sprite;
    private fixIcon: Sprite;
    private glow: Sprite;
    private buildingInfo: BuildingInfo;
    private bottomButtonBox: HBox;

    private buildingProgressProperty: BuildingProgress;
    private addressPlaceholderBackground: Graphics;
    private building: Sprite;
    private buildingMaskContainer: Container;
    private buildingMask: Graphics;
    private roundMask: Graphics;
    private road: Sprite;
    private arcGradient: ArcGradient;
    private selectItemArrow: Sprite;
    private upgradeButton: UpgradeButton;
    private buildingClicked: boolean;
    private downMousePosY: number;

    private transitionTimeLine: gsap.core.Timeline;
    private state: BuildingState = BuildingState.Default;

    public constructor(myBuildingsResources: MyBuildingsResourcePackage,
        upgradeResources: UpgradeResourcePackage,
        commonResources: CommonResourcesPackage,
        myBuildingsDynamicResources: DynamicResourcesPackage,
        buildingModel: IBuildingModel,
        referenceWidth: number,
        renderer: Renderer,
        playerModel: IPlayerModel,
        isFirstShow: boolean
    ) {
        super();
        this.myBuildingsResources = myBuildingsResources;
        this.upgradeResources = upgradeResources;
        this.commonResources = commonResources;
        this.myBuildingsDynamicResources = myBuildingsDynamicResources;
        this.buildingModel = buildingModel;
        this.referenceWidth = referenceWidth;
        this.renderer = renderer;
        this.playerModel = playerModel;
        this.isFirstShow = isFirstShow;
        this.onBuildingClick = new Event<void>();
        this.buildingModel.propertyChanged.add(this.onBuildingModelPropertyChanged, this);
        this.build();
        this.layout();
        this.onMarketPlaceLinkUpdated();
        this.transitionTimeLine = this.createUpgradeTransitionTimeline();
        this.priceHistoryButton.onUp.add(this.onHistoryButtonClick, this);
        this.listInButton.onUp.add(this.onListInButtonClick, this);
        this.seeBuildingInButton.onUp.add(this.onSeeBuildingInButtonClick, this);
        this.purchaseBuildingButton.onUp.add(this.onPurchaseButtonClick, this);
    }

    public getShowAnimation(): gsap.core.Timeline {
        return this.buildingProgressProperty.showAnimation();
    }

    public async buildingProgressUpdate(): Promise<void> {
        await this.buildingProgressProperty.updateValues();
    }

    private onBuildingModelPropertyChanged(model: IBuildingModel, key: string): void {
        switch (key) {
            case nameof(model, "address"):
                this.address.text = model.address.value;
                this.adjustText();
                break;
            case nameof(model, "zipCode"):
            case nameof(model, "city"):
            case nameof(model, "borough"):
                this.location.text = `${this.buildingModel.zipCode.value}, ${this.buildingModel.city.value}, ${this.buildingModel.borough.value}`;
                this.adjustText();
                break;
            case nameof(model, "upgrades"):
            case nameof(model, "marketplaceId"):
                this.onMarketPlaceLinkUpdated();
                break
            case nameof(model, "purchaseHistory"):
                this.priceHistoryButton.visible = model.purchaseHistory.value.length > 0;
                this.updateInitialCost();
                break;
            case nameof(model, "piggyClubNftMultiplier"):
                this.piggyClubMultiplier.visible = !isNaN(model.piggyClubNftMultiplier.value) && model.piggyClubNftMultiplier.value != null;
                break;
        }
    }

    private updateInitialCost(): void {
        const purchaseHistory = this.buildingModel.purchaseHistory.value;
        this.initialCostText.visible = purchaseHistory.length > 0 && this.playerModel.buildingRights.value === BuildingRightsType.OWNER;
        if (purchaseHistory?.length > 0) {
            const firstHistory = purchaseHistory[0];
            const price = firstHistory.price.usd != null ? firstHistory.price.usd.toFixed(2) : firstHistory.price.mon.toFixed(2);
            const currency = firstHistory.price.usd != null ? "$" : "MON";
            this.initialCostText.text = `${Building.PURCHASE_PRICE}${currency}${StringFormatExtention.formatWithComma(price)}`;
            this.initialCostText.position.set(this.road.position.x + this.road.width * 0.5 - this.initialCostText.width * 0.5, this.road.position.y + 23);
        }
    }

    private build(): void {
        this.buildingContainer = new Container();
        this.buildingProgressProperty = new BuildingProgress(this.upgradeResources, this.buildingModel);
        this.addChild(this.buildingProgressProperty);

        this.building = new Sprite(this.myBuildingsDynamicResources.resource(this.buildingModel.imageUrl.value));
        this.building.width = this.building.height = Building.BUILDING_SIDE_SIZE;
        this.building.interactive = true;
        this.building.on("pointerdown", this.buildingTouchStartHandle, this);
        this.building.on("pointerup", this.buildingTouchEndHandle, this);
        this.building.on("pointermove", this.buildingTouchMoveHandle, this);
        if (this.buildingModel.upgrades?.length === 0) {
            this.building.eventMode = "none";
        }
        this.buildingMaskContainer = new MaskContainer();

        this.buildingMask = new Graphics();
        this.buildingMask.beginFill(0xffffff);
        this.buildingMask.drawRoundedRect(0, 0, 600, Building.RECTANGLE_MASK_HEIGHT, 22);
        this.buildingMask.endFill();
        this.buildingMaskContainer.addChild(this.buildingMask);

        this.roundMask = new Graphics();
        this.roundMask.beginFill(0xffffff);
        this.roundMask.drawCircle(0, 0, Building.ROUND_MASK_RADIUS);
        this.roundMask.endFill();
        this.roundMask.scale.set(0, 0);
        this.buildingMaskContainer.addChild(this.roundMask);

        this.buildingContainer.addChild(this.buildingMaskContainer);

        this.road = new Sprite(this.myBuildingsResources.assets_views_mybuildings_ui_building_road_png);
        this.road.width = 600;
        this.road.height = 312;

        this.buildingContainer.addChild(this.building);
        this.buildingContainer.addChild(this.road);

        this.buildingContainer.mask = this.buildingMaskContainer;

        this.addressPlaceholderBackground = new Graphics();
        this.addressPlaceholderBackground.beginFill(0xffffff);
        this.addressPlaceholderBackground.drawRect(0, 0, 236, 63);
        this.addressPlaceholderBackground.endFill();

        this.addressPlaceholder = new Sprite(this.myBuildingsResources.assets_views_mybuildings_ui_adress_placeholder_png);

        this.address = new Text(this.buildingModel.address.value, new StreetTextStyle());
        this.addressPlaceholder.addChild(this.address);

        this.addressPlaceholderBackground.addChild(this.addressPlaceholder);
        this.buildingContainer.addChild(this.addressPlaceholderBackground);

        this.locationPlaceholder = new Graphics();
        this.locationPlaceholder.beginFill(0x1F1F1F);
        this.locationPlaceholder.drawRect(0, 0, 236, 35);
        this.locationPlaceholder.endFill();

        this.location = new Text(`${this.buildingModel.zipCode.value}, ${this.buildingModel.city.value}, ${this.buildingModel.borough.value}`, new ZipTextStyle());
        this.locationPlaceholder.addChild(this.location);
        this.buildingContainer.addChild(this.locationPlaceholder);

        const piggyTexture = this.buildingModel.piggyClubImageUrl.value != null && this.buildingModel.piggyClubImageUrl.value != "" ?
            this.myBuildingsDynamicResources.resource<Texture>(this.buildingModel.piggyClubImageUrl.value) : Texture.EMPTY;
        this.piggyClubMultiplier = new PiggyClubMultiplier(this.commonResources, piggyTexture, `+${((this.buildingModel.piggyClubNftMultiplier.value ?? 0) * 100)}%`);
        this.buildingContainer.addChild(this.piggyClubMultiplier);
        this.piggyClubMultiplier.visible = !isNaN(this.buildingModel.piggyClubNftMultiplier.value) && this.buildingModel.piggyClubNftMultiplier.value != null;

        this.initialCostText = new Text("", new InitialCostTextStyle());
        this.buildingContainer.addChild(this.initialCostText);
        this.updateInitialCost();

        this.priceHistoryButton = new PriceHistoryButton(this.myBuildingsResources.assets_views_mybuildings_ui_Price_history_icon_png);
        this.priceHistoryButton.visible = this.buildingModel.purchaseHistory.value.length > 0;
        const shape = new Sprite(this.myBuildingsResources.assets_views_mybuildings_ui_price_history_shape_png);
        this.priceHistoryButton.addChild(shape);
        shape.position.set(-shape.width * 0.2, -12);
        this.buildingContainer.addChild(this.priceHistoryButton);

        this.listInButton = new ListInButton(this.commonResources, this.myBuildingsResources, this.buildingModel.minBuildingLevelToAddToMp.value);
        this.listInButton.setLocked(true);

        this.seeBuildingInButton = new SeeBuildingInButton(this.commonResources);

        this.purchaseBuildingButton = new PurchaseBuildingButton(this.commonResources, this.buildingModel.priceInUsd.value);
        this.buildingContainer.addChild(this.purchaseBuildingButton);

        this.buildingGlow = new Sprite(this.myBuildingsResources.assets_views_mybuildings_ui_building_glow_png);
        this.addChild(this.buildingGlow);

        this.glow = new Sprite(this.myBuildingsResources.assets_views_mybuildings_ui_glow_in_marketplace_png);
        this.addChild(this.glow);

        this.buildingInfo = new BuildingInfo(this.commonResources, this.buildingModel, this.buildingMask.width - 48);

        this.buildingContainer.addChild(this.buildingInfo);
        this.addChild(this.buildingContainer);

        this.arcGradient = new ArcGradient(this.renderer, this.myBuildingsResources, Building.ROUND_MASK_RADIUS);
        this.arcGradient.scale.set(0, 0);
        this.addChild(this.arcGradient);

        this.selectItemArrow = new Sprite(this.myBuildingsResources.assets_views_mybuildings_ui_upgrade_arrow_png);
        this.selectItemArrow.scale.set(1, 0);
        this.addChild(this.selectItemArrow);

        this.fixIcon = new Sprite(this.myBuildingsResources.assets_views_mybuildings_ui_fix_png);
        this.fixIcon.visible = this.buildingModel.needToFix.value;
        this.addChild(this.fixIcon);

        this.bottomButtonBox = new HBox(25);
        this.addChild(this.bottomButtonBox);

        this.bottomButtonBox.addChild(this.listInButton, this.seeBuildingInButton, this.purchaseBuildingButton);

        if (this.buildingModel.upgrades?.length !== 0) {
            this.upgradeButton = new UpgradeButton(this.commonResources);
            this.upgradeButton.onUp.add(this.onUpgradeButtonClickHandle, this);
            this.bottomButtonBox.addChild(this.upgradeButton);
        }
        this.bottomButtonBox.alignmentStrategy();
        this.bottomButtonBox.position.set(this.road.position.x + (this.road.width - this.bottomButtonBox.width) * 0.5 + 25, 1180);
    }

    private onUpgradeButtonClickHandle(_upgradeButton: UpgradeButton, event: FederatedPointerEvent): void {
        event.stopPropagation();
        this.buildingClicked = true;
        this.enabled = false;
    }

    private buildingTouchStartHandle(event: FederatedPointerEvent): void {
        this.buildingClicked = true;
        this.downMousePosY = Math.ceil(event.data.global.y);
    }

    private buildingTouchMoveHandle(event: FederatedPointerEvent): void {
        const currentMousePosY: number = Math.ceil(event.data.global.y);
        if (Math.abs(this.downMousePosY - currentMousePosY) > 30) {
            this.buildingClicked = false;
        }
    }

    private buildingTouchEndHandle(): void {
        if (this.buildingClicked && this.state === BuildingState.Default) {
            this.enabled = false;
            this.onBuildingClick.emit();
        }
    }

    private onHistoryButtonClick(_button: PriceHistoryButton, event: FederatedPointerEvent): void {
        event.stopPropagation();
    }

    private onListInButtonClick(_button: ListInButton, event: FederatedPointerEvent): void {
        event.stopPropagation();
    }

    private onSeeBuildingInButtonClick(_button: SeeBuildingInButton, event: FederatedPointerEvent): void {
        event.stopPropagation();
    }

    private onPurchaseButtonClick(_button: PurchaseBuildingButton, event: FederatedPointerEvent): void {
        event.stopPropagation();
    }

    //TODO egorja: Can be used only from default state
    private layout(): void {
        this.building.position.set(0, this.buildingProgressProperty.height + 40);
        this.buildingMask.position.set(this.building.width * 0.5 - this.buildingMask.width * 0.5, this.building.position.y + 30);
        this.road.position.set(this.building.width * 0.5 - this.road.width * 0.5, this.building.position.y + this.building.height);
        this.addressPlaceholder.position.set(0, this.addressPlaceholderBackground.height * 0.5 - this.addressPlaceholder.height * 0.5);
        this.addressPlaceholderBackground.position.set(this.buildingMask.position.x, this.buildingMask.position.y + 20);
        this.locationPlaceholder.position.set(this.buildingMask.position.x, this.addressPlaceholderBackground.position.y + this.addressPlaceholderBackground.height);
        this.piggyClubMultiplier.position.set(this.buildingMask.position.x + this.buildingMask.width * 0.5 + 30, this.addressPlaceholderBackground.position.y + this.addressPlaceholderBackground.height);
        this.initialCostText.position.set(this.road.position.x + this.road.width * 0.5 - this.initialCostText.width * 0.5, this.road.position.y + 23);
        this.priceHistoryButton.position.set(this.road.position.x + this.road.width * 0.5 - this.priceHistoryButton.width * 0.3, this.road.position.y + 83);
        this.buildingGlow.position.set(this.buildingMask.x + (this.buildingMask.width - this.buildingGlow.width) * 0.5, this.buildingMask.y + (this.buildingMask.height - this.buildingGlow.height) * 0.5);
        this.glow.position.set(this.building.width * 0.5 - this.glow.width * 0.5, this.buildingMask.position.y + this.buildingMask.height);
        this.fixIcon.position.set(this.buildingMask.position.x + this.buildingMask.width - this.fixIcon.width + 10, this.buildingMask.position.y + 20);
        this.buildingInfo.position.set(this.buildingMask.position.x + (this.buildingMask.width - this.buildingInfo.width) * 0.5, this.buildingMask.position.y + this.buildingMask.height - this.buildingInfo.height - 10);
        this.buildingProgressProperty.position.set(this.buildingMask.position.x + (this.buildingMask.width - this.buildingProgressProperty.width) * 0.5, this.buildingProgressProperty.height + 24);
        this.adjustText();
        this.location.position.set(24, this.locationPlaceholder.height * 0.5 - this.location.height * 0.5);
        this.address.position.set(24, this.addressPlaceholder.height * 0.5 - this.address.height * 0.5 + 3);
        this.bottomButtonBox.position.set(this.road.position.x + (this.road.width - this.bottomButtonBox.width) * 0.5 + 25, 1180);
    }

    private adjustText(): void {
        if (this.location.width + 24 >= this.locationPlaceholder.width * 0.9) {
            this.location.scale.set(this.locationPlaceholder.width * 0.85 / (this.location.width + 24));
        }

        if (this.address.width + 24 >= this.addressPlaceholder.width * 0.9) {
            this.address.scale.set(this.addressPlaceholder.width * 0.85 / (this.address.width + 24));
        }
    }

    private createUpgradeTransitionTimeline(): gsap.core.Timeline {
        const timeline = new gsap.core.Timeline({ paused: true });

        const buildingMaskInitialPosition = new Point(this.building.width * 0.5 - this.buildingMask.width * 0.5, this.building.position.y + 30);
        const roundMaskInitialPosition = new Point(this.buildingMask.position.x + Building.ROUND_MASK_RADIUS - 5, buildingMaskInitialPosition.y + this.buildingMask.height - this.roundMask.height * 0.5);
        const roundMaskEndY = buildingMaskInitialPosition.y + Building.RECTANGLE_MASK_UPGRADE_HEIGHT + Building.ROUND_MASK_RADIUS * 0.15;

        const addressPlaceholderBackgroundInitialPosition = new Point(buildingMaskInitialPosition.x, buildingMaskInitialPosition.y + 20);
        const locationPlaceholderInitialPosition = new Point(buildingMaskInitialPosition.x, addressPlaceholderBackgroundInitialPosition.y + this.addressPlaceholderBackground.height);
        const piggyClubInitialPosition = new Point(buildingMaskInitialPosition.x, this.addressPlaceholderBackground.position.y + 10);

        timeline.to(this.bottomButtonBox, { alpha: 0.0, duration: 0.3 }, "<");
        timeline.fromTo(this.building, { width: Building.BUILDING_SIDE_SIZE, height: Building.BUILDING_SIDE_SIZE }, { width: Building.UPGRADE_BUILDING_SIDE_SIZE, height: Building.UPGRADE_BUILDING_SIDE_SIZE, duration: 1, ease: "power2.out" }, ">");
        timeline.fromTo(this.building.position, { x: 0, y: this.buildingProgressProperty.height + 40 }, { x: (this.referenceWidth - Building.UPGRADE_BUILDING_SIDE_SIZE) * 0.5, y: this.buildingProgressProperty.position.y - 168, duration: 1, ease: "power2.out" }, "<");
        timeline.fromTo(this.buildingProgressProperty, { y: this.buildingProgressProperty.position.y }, { y: this.buildingProgressProperty.position.y + 30, duration: 1, ease: "power2.out" }, "<");

        timeline.fromTo(this.buildingMask, { height: Building.RECTANGLE_MASK_HEIGHT }, { height: Building.RECTANGLE_MASK_UPGRADE_HEIGHT, duration: 1 }, "<");
        timeline.fromTo(this.buildingMask.position,
            { x: buildingMaskInitialPosition.x, y: buildingMaskInitialPosition.y },
            { x: (Building.BUILDING_SIDE_SIZE - this.buildingMask.width) * 0.5, y: this.buildingProgressProperty.position.y + this.buildingProgressProperty.height + 52, duration: 1 }, "<");

        timeline.fromTo(this.addressPlaceholderBackground.position,
            { x: addressPlaceholderBackgroundInitialPosition.x, y: addressPlaceholderBackgroundInitialPosition.y },
            { x: (Building.BUILDING_SIDE_SIZE - this.buildingMask.width) * 0.5, y: this.buildingProgressProperty.position.y + this.buildingProgressProperty.height + 72 }, "<");

        timeline.fromTo(this.locationPlaceholder.position,
            { x: locationPlaceholderInitialPosition.x, y: locationPlaceholderInitialPosition.y },
            { x: (Building.BUILDING_SIDE_SIZE - this.buildingMask.width) * 0.5, y: this.buildingProgressProperty.position.y + this.buildingProgressProperty.height + 72 + this.addressPlaceholderBackground.height }, "<");
        timeline.fromTo(this.piggyClubMultiplier.position,
            { y: piggyClubInitialPosition.y },
            { y: this.buildingProgressProperty.position.y + this.buildingProgressProperty.height + 82 }, "<");
        timeline.fromTo(this.piggyClubMultiplier,
            { alpha: 1 },
            { alpha: 0 }, "<");
        timeline.call(() => {
            if (this.state == BuildingState.Upgrade) {
                this.bottomButtonBox.removeChild(this.seeBuildingInButton);
                this.bottomButtonBox.removeChild(this.listInButton);
                this.buildingContainer.addChildAt(this.listInButton, 4);
                this.buildingContainer.addChildAt(this.seeBuildingInButton, 4);
                this.listInButton.position.set((Building.BUILDING_SIDE_SIZE - 465) * 0.5, this.road.position.y + 33);
                this.seeBuildingInButton.position.set((Building.BUILDING_SIDE_SIZE - 465) * 0.5, this.road.position.y + 33);
                this.listInButton.alpha = 0;
                this.seeBuildingInButton.alpha = 0;
            }
            else {
                this.buildingContainer.removeChild(this.seeBuildingInButton);
                this.buildingContainer.removeChild(this.listInButton);
                this.bottomButtonBox.addChildAt(this.seeBuildingInButton, 0);
                this.bottomButtonBox.addChildAt(this.listInButton, 0);
                this.seeBuildingInButton.position.set(0, 0);
                this.listInButton.position.set(0, 0);
                this.listInButton.alpha = 1;
                this.seeBuildingInButton.alpha = 1;
            }
            this.bottomButtonBox.alignmentStrategy();
        }, null, "<"
        );
        timeline.to(this.glow, { alpha: 0.0, duration: 0.1 }, "<");
        timeline.to(this.glow.position, { y: buildingMaskInitialPosition.y + Building.RECTANGLE_MASK_HEIGHT, duration: 0.01 }, "<");

        timeline.fromTo(this.roundMask.position, { x: roundMaskInitialPosition.x, y: roundMaskInitialPosition.y }, { y: roundMaskEndY, duration: 1 }, "<");
        timeline.to(this.roundMask.scale, { x: 1, y: 0.25, duration: 0.01 }, "<");
        timeline.to(this.roundMask.scale, { y: 1, duration: 1 }, "<");

        timeline.fromTo(this.arcGradient.position, { x: roundMaskInitialPosition.x, y: roundMaskInitialPosition.y }, { y: roundMaskEndY, duration: 1 }, "<");
        timeline.to(this.arcGradient.scale, { x: 1, y: 0.25, duration: 0.01 }, "<");
        timeline.to(this.arcGradient.scale, { y: 1, duration: 1 }, "<");

        timeline.to(this.selectItemArrow.position, { x: this.roundMask.position.x - this.selectItemArrow.width * 0.5, y: roundMaskEndY + Building.ROUND_MASK_RADIUS, duration: 0.0 }, ">");
        timeline.fromTo(this.selectItemArrow.scale, { x: 1.0, y: 0.0 }, { x: 1.0, y: 1.0, duration: 0.3 }, ">");

        timeline.call(() => this.state === BuildingState.Upgrade ? this.listInButton.showUpgradeStyle() : this.listInButton.showDefaultStyle(), null, ">");
        timeline.call(() => this.state === BuildingState.Upgrade ? this.seeBuildingInButton.showUpgradeStyle() : this.seeBuildingInButton.showDefaultStyle(), null, ">");

        timeline.to(this.listInButton.position, { y: roundMaskEndY + Building.ROUND_MASK_RADIUS - this.seeBuildingInButton.defaultView.height + 10, duration: 0.5 }, "<");
        timeline.to(this.seeBuildingInButton.position, { y: roundMaskEndY + Building.ROUND_MASK_RADIUS - this.seeBuildingInButton.defaultView.height + 10, duration: 0.5 }, "<");
        timeline.to(this.listInButton, { alpha: 1, duration: 0.3 }, "<");
        timeline.to(this.seeBuildingInButton, { alpha: 1, duration: 0.3 }, "<");

        timeline.call(() => this.glow.texture = this.state === BuildingState.Upgrade ?
            this.myBuildingsResources.assets_views_mybuildings_ui_glow_in_marketplace_png : this.buildingModel.needToFix.value
                ? this.myBuildingsResources.assets_views_mybuildings_ui_glow_fix_png : this.myBuildingsResources.assets_views_mybuildings_ui_glow_in_marketplace_png, null, "<");

        timeline.to(this.glow, { rotation: Math.PI, duration: 0.01 }, ">");
        timeline.to(this.glow.position, { y: this.buildingProgressProperty.position.y + this.buildingProgressProperty.height + this.glow.height, duration: 0.01 }, ">");
        timeline.to(this.glow, { alpha: 1.0, duration: 0.3 }, ">");
        timeline.to(this.glow.position, { x: Building.UPGRADE_BUILDING_SIDE_SIZE * 0.5, duration: 0.01 }, "<");
        timeline.to(this.glow.position, { y: this.buildingProgressProperty.position.y + this.buildingProgressProperty.height + 52, duration: 0.3 }, "<");

        return timeline;
    }

    public async showDefaultStyle(): Promise<void> {
        if (this.state === BuildingState.Default) {
            console.log("Building already in default state");
            return;
        }
        this.state = BuildingState.Default;
        await this.transitionTimeLine.reverse();
        this.onMarketPlaceLinkUpdated();
        this.enabled = true;
        if (this.buildingModel.level.value >= 3) {
            this.enabledButtons = true;
        }
    }

    public async showUpgradeStyle(): Promise<void> {
        if (this.state === BuildingState.Upgrade) {
            console.log("Building already in upgrade state");
            return;
        }
        this.state = BuildingState.Upgrade;
        this.fixIcon.visible = false;
        this.buildingGlow.visible = false;
        this.buildingContainer.filters = [];
        await this.buildingInfo.hideInfoTooltip();
        await this.transitionTimeLine.play();
        this.enabled = false;
        if (this.buildingModel.level.value >= 3) {
            this.enabledButtons = true;
        }
    }

    private onMarketPlaceLinkUpdated(): void {
        let glowTint = 0xffffff;
        const locked = this.buildingModel.marketplaceId.value == null;

        if (this.state === BuildingState.Upgrade) {
            this.listInButton.setLocked(!locked);
            this.purchaseBuildingButton.visible = false;
            this.purchaseBuildingButton.eventMode = "none";

            this.bottomButtonBox.alignmentStrategy();
            this.bottomButtonBox.position.set(this.road.position.x + (this.road.width - this.bottomButtonBox.width) * 0.5 + 25, 1180);
            return;
        }

        if (this.playerModel.buildingRights.value === BuildingRightsType.RENTER) {
            this.purchaseBuildingButton.setActive(this.buildingModel.priceInUsd.value > 0);
            this.listInButton.visible = false;
            this.listInButton.eventMode = "none";
            this.seeBuildingInButton.visible = false;
            this.seeBuildingInButton.eventMode = "none";
            this.buildingGlow.visible = false;
            this.buildingContainer.filters = [];
        } else {
            this.purchaseBuildingButton.setActive(false);
            if (locked) {
                this.seeBuildingInButton.visible = false;
                this.listInButton.visible = true;
                this.buildingContainer.filters = [];
                this.buildingGlow.visible = false;
                glowTint = 0xd2f6ff;
            } else {
                this.buildingContainer.filters = [new OutlineFilter(2, 0xffffff, 1, 1, false)];
                this.listInButton.visible = false;
                this.seeBuildingInButton.visible = true;
                this.buildingGlow.visible = true;
            }
            this.listInButton.setLocked(this.buildingModel.level.value < this.buildingModel.minBuildingLevelToAddToMp.value);
        }
        if (this.isFirstShow) {
            this.listIn.eventMode = "none";
            this.seeBuilding.eventMode = "none";
        }

        this.fixIcon.visible = this.buildingModel.needToFix.value;
        this.glow.texture = this.buildingModel.needToFix.value ? this.myBuildingsResources.assets_views_mybuildings_ui_glow_fix_png : this.myBuildingsResources.assets_views_mybuildings_ui_glow_in_marketplace_png;
        this.glow.tint = glowTint;
        this.bottomButtonBox.alignmentStrategy();
        this.bottomButtonBox.position.set(this.road.position.x + (this.road.width - this.bottomButtonBox.width) * 0.5, 1180);
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.transitionTimeLine.kill();
        this.building.off("pointerdown", this.buildingTouchStartHandle, this);
        this.building.off("pointerup", this.buildingTouchEndHandle, this);
        this.building.off("pointermove", this.buildingTouchMoveHandle, this);
        this.upgradeButton?.onUp.remove(this.onUpgradeButtonClickHandle, this);
        this.priceHistoryButton.onUp.remove(this.onHistoryButtonClick, this);
        this.listInButton.onUp.remove(this.onListInButtonClick, this);
        this.seeBuildingInButton.onUp.remove(this.onSeeBuildingInButtonClick, this);
        this.purchaseBuildingButton.onUp.remove(this.onPurchaseButtonClick, this);
        this.buildingModel.propertyChanged.remove(this.onBuildingModelPropertyChanged, this);
        super.destroy(options);
    }
}

class StreetTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            align: "center",
            fill: "#ffffff",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 48
        }));
    }
}

class ZipTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            align: "center",
            fill: "#B9B2CC",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 24
        }));
    }
}

class InitialCostTextStyle extends TextStyle {
    public constructor() {
        super({
            fontFamily: GameFont.DEFAULT_FONT,
            fill: [
                "#a8fd09",
                "#15ce14"
            ],
            fontSize: 42,
            align: "center",
        });
    }
}

enum BuildingState {
    Default,
    Upgrade
}